import { MouseEvent } from 'react';
import { useRouter } from 'next/router';
import dayjs from 'dayjs';
import cn from 'classnames';
import { UiIcon, UiText } from 'znanie-uikit';

import { useMediaQueries } from 'hooks/utils/useMediaQueries';

import { Link } from 'components/common/Link';
import Cover from 'components/common/Cover';

import ContentTypeIcon from 'components/widget/ContentTypeIcon';
import ContentSpeakersAvatars from 'components/widget/ContentSpeakersAvatars';
import MaterialExtension from 'components/widget/MaterialExtension';

import { ContentType, ContentTypeEnum, SpeakerType } from 'graphql/types';

import css from './ContentCard.module.scss';

interface IProps {
  item: ContentType;
  cardIndex?: number;

  onClick?(url: string, item: ContentType): void;
}

const ContentCard = ({ item, cardIndex, onClick }: IProps) => {
  const { pathname } = useRouter();
  const { isMobile } = useMediaQueries();

  const getBadgeLabel = (type: string) => {
    switch (type) {
      case ContentTypeEnum.Video:
        return 'Видео';
      case ContentTypeEnum.Article:
        return 'Статья';
      case ContentTypeEnum.Material:
        return 'Документ';
      default:
        return '';
    }
  };

  const contentCardLink = `/library/${item?.type?.toLocaleLowerCase()}/${
    item.slug
  }`;

  // передаем ссылку на видео/статью в Библиотеке знаний или
  // не передаем, если это презентация/документ
  const linkProp = item.type !== ContentTypeEnum.Material && {
    link: contentCardLink,
  };

  // обработчик клика на заголовок контента, иконку "Скачать" или обложку
  const handleClick = (event: MouseEvent<HTMLAnchorElement>, url: string) => {
    event.preventDefault();
    onClick && onClick(url, item);
  };

  return (
    <div className={css.block}>
      {item.speakers && item.speakers.length > 0 && (
        <div className={css.header}>
          <ContentSpeakersAvatars speakers={item.speakers as SpeakerType[]} />
        </div>
      )}
      <Cover
        className={cn(
          css.img,
          isMobile && item.type === ContentTypeEnum.Video
            ? css.imgSmall
            : cardIndex && cardIndex % 2 === 1
            ? css.imgBig
            : css.imgSmall
        )}
        vertical="top"
        background={item.coverUrl || ''}
        imgProxyProps={{
          sourceWidth: 800,
          sourceHeight: 450,
          resizingType: 'fill',
          gravityType: 'ce',
        }}
        {...linkProp}
        onClick={(event) => handleClick(event, linkProp ? linkProp.link : '')}
      >
        <Cover.Top
          widgets={{
            right:
              item.type !== ContentTypeEnum.Material ? (
                <ContentTypeIcon type={item.type || ''} />
              ) : (
                <MaterialExtension fileName={item.documentUrl || ''} />
              ),
          }}
        />
        {item.type === ContentTypeEnum.Material && (
          // скачиваем файл, если юзер авторизован или зареган,
          // иначе показываем модалку с сообщением о необходимости
          // регистрации/авторизации
          <Link
            href={(item.documentUrl as string) || ''}
            className={css.downloadLink}
            onClick={(event) => handleClick(event, item.documentUrl || '')}
          >
            <UiIcon
              name="download"
              size={51}
              className={css.downloadIcon}
              color="white"
            />
          </Link>
        )}
      </Cover>
      <div className={css.info}>
        <div className={css.typeBadge}>{getBadgeLabel(item.type || '')}</div>
        {item.type !== ContentTypeEnum.Material ? (
          <Link
            href={contentCardLink}
            onClick={(event) => handleClick(event, contentCardLink)}
          >
            <UiText
              className={css.title}
              truncateProps={{
                clamp: isMobile && pathname === '/' ? 2 : 4,
                useNativeClamp: true,
              }}
            >
              {item.title}
            </UiText>
          </Link>
        ) : (
          <Link
            // скачиваем файл, если юзер авторизован или зареган,
            // иначе показываем модалку с сообщением о необходимости
            // регистрации/авторизации
            href={(item.documentUrl as string) || ''}
            onClick={(event) => handleClick(event, item.documentUrl || '')}
          >
            <UiText
              className={css.title}
              truncateProps={{
                clamp: isMobile && pathname === '/' ? 2 : 4,
                useNativeClamp: true,
              }}
            >
              {item.title}
            </UiText>
          </Link>
        )}
        <div className={css.date}>{dayjs(item.created).fromNow()}</div>
      </div>
    </div>
  );
};

export default ContentCard;
